import { format, parseISO } from "date-fns";
import { RotateCcw } from "lucide-react";
import { LogExport } from "@/components/admin/LogExport";

export function AuditTab({ audit, auditBusy, loadAudit, auditIsRestorable, restoreAudit, restoringAuditId }) {
  return (
    <div className="space-y-4">
      <div className="card-tactical rounded-md p-5">
        <div className="flex items-baseline justify-between flex-wrap gap-3">
          <div>
            <div className="font-display font-bold text-2xl tracking-tight">Audit log</div>
            <p className="text-sm opacity-60 mt-1">Every change to leagues, referees, teams, pitches, venues, roles — most recent first. Deletes, cancellations and edits can be restored with one click.</p>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <LogExport kind="audit" />
            <button onClick={loadAudit} data-testid="audit-refresh" className="text-[10px] tracking-widest uppercase px-3 py-1.5 border border-border hover:border-[#83B68B] hover:text-[#83B68B] rounded transition-colors">{auditBusy ? "Loading…" : "Refresh"}</button>
          </div>
        </div>
      </div>
      <div className="card-tactical rounded-md overflow-hidden overflow-x-auto">
        {!audit || audit.length === 0 ? (
          <div className="p-8 text-sm opacity-60 text-center">No audit entries yet — make a change to see it here.</div>
        ) : (
          <table className="w-full text-sm" data-testid="audit-table">
            <thead className="bg-muted text-[10px] tracking-widest uppercase opacity-60">
              <tr>
                <th className="text-left px-4 py-3">When</th>
                <th className="text-left px-4 py-3">Actor</th>
                <th className="text-left px-4 py-3">Entity</th>
                <th className="text-left px-4 py-3">Action</th>
                <th className="text-left px-4 py-3">Notes</th>
                <th className="text-right px-4 py-3">Undo</th>
              </tr>
            </thead>
            <tbody>
              {audit.map(a => (
                <tr key={a.id} className="border-t border-border" data-testid={`audit-row-${a.id}`}>
                  <td className="px-4 py-3 whitespace-nowrap opacity-80">{format(parseISO(a.at), "d MMM yyyy · HH:mm")}</td>
                  <td className="px-4 py-3">{a.actor_name || "—"}</td>
                  <td className="px-4 py-3"><span className="text-[10px] tracking-widest uppercase px-1.5 py-0.5 rounded bg-muted mr-2 opacity-80">{a.entity_type}</span>{a.entity_name || a.entity_id || ""}</td>
                  <td className="px-4 py-3">
                    <span className={`text-[10px] tracking-widest uppercase px-1.5 py-0.5 rounded ${a.action === "delete" || a.action === "cancel" ? "bg-red-500/20 text-red-500" : a.action === "restore" ? "bg-blue-500/20 text-blue-400" : "bg-[#83B68B]/20 text-[#83B68B]"}`}>{a.action}</span>
                    {a.reverted && <span className="ml-2 text-[10px] tracking-widest uppercase px-1.5 py-0.5 rounded bg-muted opacity-70" data-testid="audit-reverted-badge">reverted</span>}
                  </td>
                  <td className="px-4 py-3 text-xs opacity-70">{a.notes || "—"}</td>
                  <td className="px-4 py-3 text-right">
                    {auditIsRestorable(a) ? (
                      <button
                        onClick={() => restoreAudit(a)}
                        disabled={restoringAuditId === a.id}
                        data-testid={`audit-restore-${a.id}`}
                        className="text-[10px] tracking-widest uppercase px-3 py-1.5 border border-border hover:border-[#83B68B] hover:text-[#83B68B] rounded inline-flex items-center gap-1.5 transition-colors disabled:opacity-50"
                      >
                        <RotateCcw className="w-3 h-3" />
                        {restoringAuditId === a.id ? "Restoring…" : "Restore"}
                      </button>
                    ) : (
                      <span className="text-[10px] opacity-30">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
